import { useState, useRef, useEffect } from "react";
import { Plus, ChevronDown, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const REPORT_ITEMS = [
  { label: "Spray Charts", path: (id) => `/scouting/reports/${id}/spraycharts` },
  { label: "Baserunning", path: (id) => `/scouting/reports/${id}/baserunning` },
  { label: "Hitting Splits", path: (id) => `/scouting/reports/${id}/hitting-splits` },
  { label: "Pitching Splits", path: (id) => `/scouting/reports/${id}/pitching-splits` },
  { label: "Players to Watch", path: (id) => `/scouting/reports/${id}/players-to-watch` },
  { label: "Depth Chart", path: (id) => `/scouting/reports/${id}/depth-chart` },
];

export const ReportsList = (props) => {
  const { reports, onCreateClick, onDeleteReport, onReportSelect } = props;
  const navigate = useNavigate();
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const dropdownRef = useRef({});

  useEffect(() => {
    if (!openDropdownId) return;
    const handleClickOutside = (e) => {
      if (!dropdownRef.current[openDropdownId]?.contains(e.target)) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdownId]);

  const handleDelete = (report) => {
    toast((t) => (
      <div className="flex flex-col gap-3">
        <p>Delete report for <strong>{report.teamName}</strong>?</p>
        <div className="flex gap-2">
          <button
            className="px-3 py-1.5 text-sm text-white bg-red-500 rounded-lg hover:bg-red-600"
            onClick={() => { onDeleteReport(report.id); toast.dismiss(t.id); }}
          >
            Delete
          </button>
          <button
            className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            onClick={() => toast.dismiss(t.id)}
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: 5000 });
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  };

  const ReportCard = ({ report }) => {
    const isOpen = openDropdownId === report.id;
    return (
      <div className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 hover:shadow-sm transition-all">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">{report.teamName}</h3>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-gray-500">
              <span className="font-medium text-gray-700">{report.year}</span>
              <span>·</span>
              <span>{report.numPitchers} pitchers, {report.numHitters} hitters</span>
              {formatDate(report.dateCreated) && (
                <>
                  <span className="hidden sm:inline">·</span>
                  <span className="hidden sm:inline text-gray-400">Created {formatDate(report.dateCreated)}</span>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => onReportSelect(report)}
              className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Open
            </button>

            <div
              className="relative"
              ref={(el) => { if (el) dropdownRef.current[report.id] = el; }}
            >
              <button
                onClick={() => setOpenDropdownId(isOpen ? null : report.id)}
                className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors inline-flex items-center gap-1"
              >
                Reports
                <ChevronDown size={13} className={`transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`} />
              </button>

              {isOpen && (
                <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg border border-gray-200 shadow-md py-1 z-50">
                  {REPORT_ITEMS.map(({ label, path }) => (
                    <button
                      key={label}
                      onClick={() => { setOpenDropdownId(null); navigate(path(report.id)); }}
                      className="w-full px-3 py-2 text-xs text-left text-gray-700 hover:bg-gray-50"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => handleDelete(report)}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200">
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={onCreateClick}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors font-medium"
        >
          <Plus size={16} />
          New Report
        </button>
      </div>

      {reports.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-gray-200 rounded-lg">
          <p className="text-gray-500">No reports created yet</p>
          <p className="text-gray-400 mt-1 text-sm">Click &ldquo;New Report&rdquo; to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((report) => (
            <ReportCard key={report.id} report={report} />
          ))}
        </div>
      )}
    </div>
  );
};
